import parse from 'url-parse';
import { trace, SpanKind, SpanStatusCode, propagation, context } from '@opentelemetry/api';

import { sanitizePathname } from './sanitizePathname.js';
import { getBaseURL } from './getBaseURL.js';

const tracer = trace.getTracer('clippy-frontend');

const traceRequestSuccess = (rootSpan, rootContext) => (config) => {
  const modifiedConfig = { ...config };
  const { baseURL, url } = config;

  const base = getBaseURL(baseURL);
  const parsedUrl = parse(url, base);
  const pathname = sanitizePathname(parsedUrl.pathname);

  try {
    const span = tracer.startSpan(
      `${config.method.toUpperCase()} ${pathname}`,
      { kind: SpanKind.CLIENT, attributes: {
        'http.method': config.method.toUpperCase(),
        'http.url': pathname,
      }},
      rootContext,
    );

    const headers = { ...config.headers };
    propagation.inject(trace.setSpan(context.active(), span), headers);
    modifiedConfig.headers = headers;
    modifiedConfig.span = span;
  } catch (error) {
    throw new Error(`Request success tracing failed: ${error}`);
  }
  return modifiedConfig;
};

const traceRequestError = (requestError) => {
  if (requestError.config?.span) {
    try {
      requestError.config.span.setStatus({ code: SpanStatusCode.ERROR, message: 'request error' });
      requestError.config.span.end();
    } catch (error) {
      throw new Error(`Request error tracing failed: ${error}`);
    }
  }
  return Promise.reject(requestError);
};

const traceResponseSuccess = (response) => {
  if (response.config?.span) {
    try {
      response.config.span.setAttribute('http.status_code', response.status);
      response.config.span.end();
    } catch (error) {
      throw new Error(`Response success tracing failed: ${error}`);
    }
  }
  return response;
};

const traceResponseError = (responseError) => {
  if (responseError.config?.span) {
    try {
      responseError.config.span.setStatus({ code: SpanStatusCode.ERROR });
      responseError.config.span.setAttribute('http.status_code', responseError.code);
      responseError.config.span.end();
    } catch (error) {
      throw new Error(`Response error tracing failed: ${error}`);
    }
  }
  return Promise.reject(responseError);
};

export const applyTracing = (axiosInstance, options = {}) => {
  if (!axiosInstance) {
    return null;
  }

  const { span, spanName } = options;
  if (!span && !spanName) {
    return null;
  }

  const rootSpan = span || tracer.startSpan(spanName);
  const rootContext = trace.setSpan(context.active(), rootSpan);

  axiosInstance.interceptors.request.use(traceRequestSuccess(rootSpan, rootContext), traceRequestError);
  axiosInstance.interceptors.response.use(traceResponseSuccess, traceResponseError);

  return rootSpan;
};
